import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IconPencil, IconPlus, IconSearch, IconTag, IconTrash } from '@tabler/icons-react';
import type { Group } from '../../../../entities/group';
import type { Tag } from '../../../../entities/tag';
import { pb } from '../../../../shared/api/pocketbase';
import { logAction } from '../../../../shared/lib/userLogger';
import { Button } from '../../../../shared/ui/Button';
import { Input } from '../../../../shared/ui/Input';
import { TagModal } from '../../../../features/admin-tag-form';

const pluralTags = (n: number) => {
  if (n === 1) return '1 тег';
  if (n >= 2 && n <= 4) return `${n} тега`;
  return `${n} тегов`;
};

const TagsSection: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; tag?: Tag } | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadTags = useCallback((q: string) => {
    setLoading(true);
    const safe = q.trim().replace(/"/g, '');
    const filter = safe ? `name ~ "${safe}"` : undefined;
    pb.collection('tags')
      .getFullList<Tag>({ sort: 'name', expand: 'groupId', filter })
      .then(setTags)
      .catch(() => setTags([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadTags('');
    pb.collection('groups')
      .getFullList<Group>({ sort: 'name', fields: 'id,name' })
      .then(setGroups)
      .catch(() => setGroups([]));
  }, [loadTags]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => loadTags(value), 300);
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      const tagName = tags.find(t => t.id === id)?.name || id;
      await pb.collection('tags').delete(id);
      logAction(`Deleted tag: ${tagName}`, 'REMOVE');
      setConfirmDeleteId(null);
      loadTags(search);
    } catch {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  const grouped = React.useMemo(() => {
    const map = new Map<string, { label: string; tags: Tag[] }>();
    for (const tag of tags) {
      const expanded = tag.expand?.groupId;
      const groupId = tag.groupId || '__none__';
      const label = expanded?.name
        || groups.find(g => g.id === tag.groupId)?.name
        || (tag.groupId ? `Группа ${tag.groupId.slice(-4)}` : 'Без группы');
      if (!map.has(groupId)) map.set(groupId, { label, tags: [] });
      map.get(groupId)!.tags.push(tag);
    }
    return Array.from(map.values());
  }, [tags, groups]);

  return (
    <div className="adm-section">
      <div className="adm-section-header adm-section-header-row">
        <div>
          <h2 className="adm-section-title">Теги</h2>
          <p className="adm-section-sub">{loading ? 'Загрузка...' : pluralTags(tags.length)}</p>
        </div>
        <div className="adm-ut-toolbar">
          <div className="adm-input-wrap" style={{ width: 220 }}>
            <span className="adm-input-icon"><IconSearch size={15} stroke={2} /></span>
            <Input
              className="adm-input"
              placeholder="Поиск..."
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
            />
          </div>
          <Button variant="adm-add" type="button" onClick={() => setModal({ mode: 'add' })}>
            <IconPlus size={15} stroke={2.5} />Добавить тег
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : tags.length === 0 ? (
        <div className="adm-empty" style={{ padding: '24px 0', textAlign: 'center' }}>Теги не созданы</div>
      ) : (
        <div className="adm-tags-cards">
          {grouped.map(group => (
            <div key={group.label} className="adm-tag-group-block">
              <div className="adm-tags-group-label">{group.label}</div>
              <div className="adm-tag-chips-list">
                {group.tags.map(tag => {
                  const isConfirming = confirmDeleteId === tag.id;
                  const chipStyle = tag.colors
                    ? { background: tag.colors.bg, color: tag.colors.text, borderColor: tag.colors.bg }
                    : undefined;
                  const dotStyle = tag.colors ? { color: tag.colors.dot } : undefined;
                  return (
                    <div key={tag.id} className="adm-tag-chip-row">
                      <span className="adm-tag-chip" style={chipStyle}>
                        <IconTag size={11} stroke={2} style={dotStyle} />
                        {tag.name}
                      </span>
                      {isConfirming ? (
                        <>
                          <Button variant="adm-cancel" size="sm" type="button" onClick={() => setConfirmDeleteId(null)}>
                            Отмена
                          </Button>
                          <Button variant="danger" size="sm" type="button" disabled={deleting} onClick={() => handleDelete(tag.id)}>
                            {deleting ? '...' : 'Удалить'}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button variant="adm-edit" type="button" title="Редактировать" onClick={() => setModal({ mode: 'edit', tag })}>
                            <IconPencil size={13} stroke={2} />
                          </Button>
                          <Button variant="adm-delete" type="button" title="Удалить" onClick={() => setConfirmDeleteId(tag.id)}>
                            <IconTrash size={13} stroke={2} />
                          </Button>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <TagModal
          mode={modal.mode}
          initial={modal.tag ?? null}
          groups={groups}
          onClose={() => setModal(null)}
          onSaved={() => loadTags(search)}
        />
      )}
    </div>
  );
};

export { TagsSection };
